import React from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormHelperText,
  Dialog,
  Backdrop
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import "./CohortRuleCreation.css";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingSpinner from "../../../Loading/component";

const CohortRuleUpdateComponent = ({
  formData,
  searchInput,
  searchResults,
  searchLoading,
  showDropdown,
  errors,
  isSubmitting,
  loading,
  ruleTypes,
  onChange,
  onSearchInputChange,
  onAddName,
  onRemoveName,
  onSlotCountChange,
  onSubmit,
  onDateChange,
  showSuccessModal,
  newRuleId,
  setShowSuccessModal,
  newRuleReferenceName,
  isMvidSearch
}) => {
  const navigate = useNavigate();

  const isSaveDisabled = () => {
    if (!formData.ruleType || !formData.referenceId || !formData.referenceType) return true;
    if (formData.ruleType === "OfferPrice") {
      return !formData.maxPrice;
    }
    return formData.names.length === 0 || !formData.validationType;
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="Rule-rule-container">
          <Sidebar />

          <div className="Rule-rule-content">
            <div className="Rule-rule-header">
              <button onClick={() => navigate(-1)} className="Rule-rule-back-button">←</button>
              <h2 className="Rule-rule-title">Edit Cohort Rule</h2>
            </div>

            <div className="Rule-rule-form">


              <div className="Rule-rule-divider"></div>

              {/* Start Date */}
              <div className="Rule-rule-section">
                <Typography className="Rule-rule-section-title">Select Start Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.startDate}
                    onChange={onDateChange}
                    minDate={new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        size="small"
                        className="Rule-rule-input"
                        disabled={isSubmitting}
                      />
                    )}
                    disabled={isSubmitting}
                  />
                </LocalizationProvider>
              </div>

              <div className="Rule-rule-divider"></div>

              {/* Rule Type */}
              <div className="Rule-rule-section">
                <Typography className="Rule-rule-section-title">Select Rule Type</Typography>
                <RadioGroup
                  name="ruleType"
                  value={formData.ruleType}
                  onChange={onChange}
                  className="Rule-rule-radio-group"
                >
                  <div className="Rule-rule-radio-grid">
                    {ruleTypes.map((type) => (
                      <FormControlLabel
                        key={type.value}
                        value={type.value}
                        control={<Radio className="Rule-rule-radio" disabled={isSubmitting} />}
                        label={
                          <div className="rule-type-option">
                            <span className="rule-type-icon-container">
                              <img src={type.icon} className="Rule-rule-type-icon" alt={type.label} />
                            </span>
                            <span className="Rule-rule-type-label">{type.label}</span>
                          </div>
                        }
                        className="Rule-rule-radio-label"
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </RadioGroup>
                {errors.ruleType && (
                  <FormHelperText error className="error-message">
                    Please select a rule type
                  </FormHelperText>
                )}
              </div>

              <div className="Rule-rule-divider"></div>

              {/* Name or Max Price */}
              <div className="Rule-rule-section">
                <Typography className="Rule-rule-section-title">
                  {formData.ruleType === "OfferPrice" ? "Max Price" : "Name"}
                </Typography>
                {formData.ruleType === "OfferPrice" ? (
                  <>
                    <TextField
                      type="text"
                      placeholder="Enter max price"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="maxPrice"
                      value={formData.maxPrice || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const regex = /^\d{0,4}(\.\d{0,2})?$/; 
                        if (value === "" || regex.test(value)) {
                          onChange(e);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                        disabled: isSubmitting
                      }}
                      className="warehouse-rule-input"
                      error={errors.maxPrice}
                      disabled={isSubmitting}
                    />
                    {errors.maxPrice && (
                      <FormHelperText error className="error-message">
                        Please enter a max price
                      </FormHelperText>
                    )}
                  </>
                ) : (
                  <>
                    <div className="Rule-rule-search-container">
                      <TextField
                        value={searchInput}
                        onChange={onSearchInputChange}
                        placeholder={`Search ${formData.ruleType === "MVID" ? "Manufacturer Variant ID" : formData.ruleType + " Name"}...`}
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: searchLoading && (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ),
                          disabled: isSubmitting || !formData.ruleType
                        }}
                        className="Rule-rule-input Rule-rule-search-input"
                        disabled={isSubmitting || !formData.ruleType}
                      />
                      {showDropdown && (
                        <div className="Rule-rule-dropdown">
                          {searchLoading ? (
                            <div className="Rule-rule-dropdown-item">
                              <CircularProgress size={20} />
                            </div>
                          ) : searchResults.length > 0 ? (
                            searchResults.map(item => (
                              <div
                                key={item.id}
                                onClick={() => onAddName(item)}
                                className="Rule-rule-dropdown-item"
                                style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                              >
                                {/* {formData.ruleType === "MVID" ? `${item.name}` : item.name} */}
                                {isMvidSearch ? (
                                  <div>
                                    <div><strong>{item.id}</strong> </div>
                                    {item.displayName && (
                                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        {item.displayName.split(' - ')[1]}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span>{item.id} <span style={{ color: "#888" }}>({item.name})</span></span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="Rule-rule-dropdown-no-results">
                              {searchInput ? "No results found" : "Start typing to search"}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="Rule-rule-selected-names">
                      {formData.names.map(item => (
                        <Chip
                          key={item.id}
                          label={formData.ruleType === "MVID" ? `${item.name} ` : item.name}
                          onDelete={isSubmitting ? undefined : () => onRemoveName(item.id)}
                          deleteIcon={<CloseIcon />}
                          className="Rule-rule-chip"
                          disabled={isSubmitting}
                        />
                      ))}
                    </div>
                    {errors.names && (
                      <FormHelperText error className="error-message">
                        Please select at least one {formData.ruleType.toLowerCase()}
                      </FormHelperText>
                    )}
                  </>
                )}
              </div>

              {formData.ruleType !== "OfferPrice" && (
                <>
                  <div className="Rule-rule-divider"></div>
                  <div className="Rule-rule-section">
                    <Typography className="Rule-rule-section-title">Select Validation Type</Typography>
                    <RadioGroup
                      name="validationType"
                      value={formData.validationType}
                      onChange={onChange}
                      className="Rule-rule-validation-group"
                    >
                      <div
                        className="Rule-rule-slot-container"
                       
                      >
                        <FormControlLabel
                          value="QUANTITY"
                          control={<Radio className="Rule-rule-radio" disabled={isSubmitting} />}
                          label="MANDATORY"
                          className="Rule-rule-radio-label"
                          disabled={isSubmitting}
                         
                        />

                        <div  className={`Rule-rule-slot-controls ${formData.validationType === "RESTRICTED" ? "disabled" : ""
                            }`}>
                          <IconButton
                            onClick={() => !isSubmitting && onSlotCountChange(-1)}
                            disabled={formData.slotCount <= 1 || isSubmitting || formData.validationType === "RESTRICTED"}
                            className="Rule-rule-slot-button"
                            // style={{
                            //   color: formData.validationType === "RESTRICTED" ? "grey" : "inherit",
                            // }}
                          >
                            <RemoveIcon />
                          </IconButton>

                          <Typography className="Rule-rule-slot-value">
                            {formData.slotCount}
                          </Typography>

                          <IconButton
                            onClick={() => !isSubmitting && onSlotCountChange(1)}
                            className="Rule-rule-slot-button"
                            disabled={isSubmitting || formData.validationType === "RESTRICTED"}
                            // style={{
                            //   color: formData.validationType === "RESTRICTED" ? "grey" : "inherit",
                            // }}
                          >
                            <AddIcon />
                          </IconButton>
                        </div>
                      </div>




                      <Typography className="Rule-rule-hint-text">
                        In case of adding multiple selections in MANDATORY, Rule will check validation for ANY 1 selection
                      </Typography>

                      <div className="Rule-rule-slot-container">
                        <FormControlLabel
                          value="RESTRICTED"
                          control={<Radio className="Rule-rule-radio" disabled={isSubmitting} />}
                          label="Restricted"
                          className="Rule-rule-radio-label"
                          disabled={isSubmitting}
                        />
                      </div>
                      <Typography className="Rule-rule-hint-text">
                        In case of adding multiple selections in RESTRICTED, Rule will check validation For ALL selections
                      </Typography>
                    </RadioGroup>
                    {errors.validationType && (
                      <FormHelperText error className="error-message">
                        Please select a validation type
                      </FormHelperText>
                    )}
                  </div>
                </>
              )}
              <Dialog
                open={showSuccessModal}
                onClose={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                PaperProps={{
                  style: { borderRadius: 20, padding: '20px 30px', textAlign: 'center' }
                }}
              >
                <CheckCircleIcon style={{ fontSize: 60, color: "#00b300", margin: '10px auto' }} />

                <Typography variant="h6" gutterBottom>
                  Successfully Updated
                </Typography>

                <Typography variant="body1">
                  Rule Updated Successfully (ID <strong>{newRuleId}</strong>)
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  For <strong> {newRuleReferenceName}</strong>
                </Typography>

                <Button
                  variant="contained"
                  sx={{ mt: 3, backgroundColor: '#00b300', '&:hover': { backgroundColor: '#009900' } }}
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  Ok
                </Button>
              </Dialog>

              {/* Action Buttons */}
              <div className="Rule-rule-actions">
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  className="Rule-rule-cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={onSubmit}
                  className="Rule-rule-save-button"
                  disabled={isSaveDisabled() || isSubmitting}
                  sx={{
                    '&.Mui-disabled': {
                      backgroundColor: '#b0b0b0',
                      color: '#fff'
                    }
                  }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Update Rule"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CohortRuleUpdateComponent;